import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { passengerCMSApi, post, remove } from "~/Services";
import { passengerHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import {
  renderActionsColumn,
  renderDateColumn,
  renderImageColumn,
  renderPublishedColumn,
} from "~/Utils/TableRenderFunction";
import { useHomepageBannerDetails } from "./Slider.hooks";

export const Slider = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(undefined);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [data, setData] = useState<any[]>([]);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");
  const [selectedPublished, setSelectedPublished] = useState<any>("");
  const [currentAirport] = useLocalStorage("currentAirport", null);

  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const publishedData: TypeData[] = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const { data: drilldown, refetch: refetchDocument } = useHomepageBannerDetails(id || "", currentAirport);

  useEffect(() => {
    if (id) refetchDocument();
  }, [id]);

  useEffect(() => {
    if (query.get("activeTab") === "sliders") {
      setSelectedPublished("");
      setSearchValue("");
      setCurrentPage(0);
    }
  }, [query.get("activeTab")]);

  // Fetch homepage banner records
  const getRecords = async () => {
    setIsLoading(true);
    try {
      const url = `/api/${currentAirport}/homepage-banners/all-fields`;
      const res = await passengerCMSApi.get(url);
      const items = res?.data;

      setData(items);
      setSortedData(items);
      setTotalRecords(items.length);
    } catch (err) {
      console.error("Failed to fetch homepage banners:", err);
      setData([]);
      setSortedData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRecords();
  }, []);

  // 🔍 Filtering Logic
  useEffect(() => {
    let filteredData = Array.isArray(data) ? [...data] : [];

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filteredData = filteredData.filter((item: any) =>
        String(item.title || "")
          .toLowerCase()
          .includes(searchLower)
      );
    }

    if (selectedPublished !== "") {
      filteredData = filteredData.filter((item: any) => item.published === selectedPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  const handleDelete = async (id: string) => {
    try {
      await remove(`${passengerHost}/api/homepage-banners/${id}`);
      getRecords();
    } catch (err) {
      console.error("Failed to delete banner", err);
    }
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Sequence",
      dataIndex: "sequence",
      key: "sequence",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "sequence"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "sequence"
            ? "descend"
            : undefined,
    },
    {
      title: "Image",
      dataIndex: "banner_image",
      key: "banner_image",
      render: renderImageColumn,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: decodeHTML,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "title"
            ? "descend"
            : undefined,
    },
    // {
    //   title: "Redirect URL",
    //   dataIndex: "redirectedURL",
    //   key: "redirectedURL",
    //   render: (url: string) =>
    //     url ? (
    //       <a
    //         href={url}
    //         target="_blank"
    //         rel="noreferrer"
    //         className="text-blue-600 underline"
    //       >
    //         {url}
    //       </a>
    //     ) : null,
    // },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "changed",
      key: "changed",
      render: (value: any, record: any) => {
        if (value && record.created && value === record.created) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "changed"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "changed"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/passenger/homepage/slider/add?editId=${record.id}`);
          },
          undefined,
          () => {
            setSelectedRowData(record);
            setDeleteModalVisible(true);
          },
          "users"
        ),
    },
  ];

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);
  const getPatchHomepageBannerApiUrl = (airport: string, id: string | number) => {
    switch (airport?.toLowerCase()) {
      case "klia1":
        return `${passengerHost}/api/homepage-banners/${id}`;

      case "klia2":
        return `${passengerHost}/api/homepage-banner-klia2-crud-data/${id}`;

      case "penang":
        return `${passengerHost}/api/homepage-banner-pen-crud-data/${id}`;

      case "kota-kinabalu":
        return `${passengerHost}/api/homepage-banner-bk-crud-data/${id}`;

      case "kuching":
        return `${passengerHost}/api/homepage-banner-kch-crud-data/${id}`;

      case "langkawi":
        return `${passengerHost}/api/homepage-banner-lgk-crud-data/${id}`;

      default:
        return "";
    }
  };

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (sortOrder === "asc" && orderByName === column.dataIndex) {
      setSortOrder("desc");
      order = "desc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);
    const tempData = [...sortedData];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (!apiUrl) return;

    const payload = {
      action,
      method,
      module: "Home Page",
      subModule: "sliders",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes,
      appList: [
        {
          appName: "PASSENGER_ECMS",
          apiUrl,
        },
      ],
      payload: data,
      status,
    };

    try {
      const response: any = await post("/management/publish", payload);
      openToast({
        content: "Data sent for review",
        type: "success",
      });

      if (response.status === "OK" && status === "PENDING") {
        navigate("/dedicated/status-information?activeTab=pending");
      }
    } catch {
      openToast({
        content: "Failed to send data",
        type: "error",
      });
    }
  };

  async function sendDeleteRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        getPatchHomepageBannerApiUrl(currentAirport, selectedRowData?.id),
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {
      console.error("Delete request failed", err);
    }
  }

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={currentPageData || []}
        exportFileName="BANNER_IMAGE"
        isLoading={isLoading}
        isPagination
        recordsPerPage={10}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport
        searchPlaceholderText="Search Title"
        onClick={(record) => setSelectedRowData(record)}
        additionalCTA={
          <ActionDropdown
            value={selectedPublished}
            options={publishedData}
            onChange={handlePublishedChange}
            placeholder="Select Published"
            className="w-48"
          />
        }
      />

      <div className="pagination">
        <Pagination
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <TableDrawer
        title={<Typography.Title level={4}>Detailed View</Typography.Title>}
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "sliders") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteRequest}
        defaultReviewNotes="Kindly review this Homepage"
      />
    </>
  );
};

// Sorting helpers
export function handleSort(a: any, b: any, order: string | undefined, sortBy: string) {
  if (["title"].includes(sortBy)) {
    const valueA = a[sortBy]?.toLowerCase() || "";
    const valueB = b[sortBy]?.toLowerCase() || "";

    if (order === "desc") return valueA < valueB ? 1 : -1;
    if (order === "asc") return valueA > valueB ? 1 : -1;
    return 0;
  }

  if (order === "desc") return a[sortBy] < b[sortBy] ? 1 : -1;
  if (order === "asc") return a[sortBy] > b[sortBy] ? 1 : -1;
  return 0;
}

export default Slider;
